/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageIcon } from '@radix-ui/react-icons';
import { Text, Image, UnstyledButton } from '@/shared/ui';
import { GalleryLightbox } from './GalleryLightbox';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';

interface PropertyGalleryProps {
  images: string[];
  coverImage?: string;
  /**
   * Receives the picked files when the listing has no photos yet. Supplying
   * it is what turns the empty frame from a sign into a place to drop a
   * photo; omit it (the public view, a read-only caller) and the frame stays
   * inert.
   */
  onAddPhotos?: (files: File[]) => void;
  /** Disables the empty frame and says so while an upload is in flight. */
  uploading?: boolean;
}

/** What the picker and the drop target accept, matching the drawer's uploader. */
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** Thumbnails shown under the cover. Four fills the row without crowding. */
const THUMBNAIL_COUNT = 4;

/**
 * Build the ordered list of unique photo URLs to display.
 *
 * The backend property record can carry the same photo URL more than once
 * (literal duplicates) and may also repeat the cover image inside `images`.
 * Counting that raw array overstates the real photo count (a 2-photo listing
 * showing "6 fotos"). This collapses `images` to first-seen unique URLs and,
 * when a cover is provided, pins it first without ever double-counting it.
 *
 * @param images - Raw image URLs from the property record (may contain duplicates).
 * @param coverImage - Optional cover URL to render as the main photo.
 * @returns Ordered unique photo URLs, cover first when present.
 */
function buildUniquePhotos(images: string[], coverImage?: string): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  if (coverImage) {
    seen.add(coverImage);
    unique.push(coverImage);
  }
  for (const img of images) {
    if (!seen.has(img)) {
      seen.add(img);
      unique.push(img);
    }
  }
  return unique;
}

/**
 * The empty frame, as a sign: nothing to open, nothing to add.
 */
const EmptyGalleryStatic: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex min-h-[200px] items-center justify-center rounded-(--radius-lg) border border-solid border-[var(--border-subtle)] bg-[var(--surface-card)] p-(--space-4)">
    <div className="flex flex-col items-center gap-(--space-2) text-(--text-tertiary-soft)">
      <ImageIcon width={26} height={26} className="opacity-60" />
      <Text className="m-0 text-[length:var(--text-meta)]">{label}</Text>
    </div>
  </div>
);

/**
 * The empty frame, as a target.
 *
 * The frame is already the biggest thing on the screen and already says the
 * photos are missing — making it the button is shorter than sending someone
 * to the editor to find the uploader, which is the trip this replaces. It
 * also takes a drag-and-drop, because a frame that says "arraste" and then
 * ignores a dropped file is worse than one that never offered.
 *
 * @param props.label - The "no photos" line.
 * @param props.hint - The call to action under it.
 * @param props.uploading - Whether an upload is already in flight.
 * @param props.onFiles - Receives the accepted image files.
 * @returns The interactive empty frame.
 */
const EmptyGalleryDropzone: React.FC<{
  label: string;
  hint: string;
  uploading: boolean;
  onFiles: (files: File[]) => void;
}> = ({ label, hint, uploading, onFiles }) => {
  const [dragging, setDragging] = useState(false);

  const accept = (list: FileList | null) => {
    const files = Array.from(list ?? []).filter((f) => ACCEPTED_TYPES.includes(f.type));
    if (files.length > 0) onFiles(files);
  };

  const openPicker = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ACCEPTED_TYPES.join(',');
    input.onchange = () => accept(input.files);
    input.click();
  };

  return (
    <UnstyledButton
      type="button"
      disabled={uploading}
      onClick={openPicker}
      onDragOver={(e: React.DragEvent) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e: React.DragEvent) => { e.preventDefault(); setDragging(false); accept(e.dataTransfer.files); }}
      aria-label={hint}
      data-testid="gallery-empty-dropzone"
      className="flex min-h-[200px] w-full cursor-pointer items-center justify-center rounded-(--radius-lg) border-2 border-dashed p-(--space-4) transition-colors disabled:cursor-wait"
      style={{
        borderColor: dragging ? 'var(--seed-base)' : 'var(--border-subtle)',
        background: dragging ? 'color-mix(in srgb, var(--seed-base) 6%, var(--surface-card))' : 'var(--surface-card)',
      }}
    >
      <div className="flex flex-col items-center gap-(--space-2) text-(--text-tertiary-soft)">
        <ImageIcon width={26} height={26} className="opacity-60" />
        <Text className="m-0 text-[length:var(--text-meta)]">{label}</Text>
        <Text
          className="m-0 text-[length:var(--text-meta)]"
          style={{ color: 'var(--seed-base)', fontWeight: 'var(--font-semibold)' as unknown as number }}
        >
          {hint}
        </Text>
      </div>
    </UnstyledButton>
  );
};

/**
 * Cover photo plus a row of four thumbnails, both opening the lightbox.
 *
 * The photo count rides on the cover rather than a caption underneath: it is
 * the first thing a broker checks before sending a listing anywhere, and a
 * line below the frame is the line nobody reads. When more photos exist than
 * fit, the last thumbnail carries the remainder instead of silently hiding
 * them.
 *
 * @param props.images - Resolved photo URLs.
 * @param props.coverImage - Optional cover pinned first.
 * @returns The rendered gallery.
 */
export const PropertyGallery: React.FC<PropertyGalleryProps> = ({ images, coverImage, onAddPhotos, uploading = false }) => {
  const { t } = useTranslation('features/properties');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  /** Which photo the cover is showing; the arrows step it. */
  const [coverIndex, setCoverIndex] = useState(0);

  const allImages = buildUniquePhotos(images, coverImage);
  if (allImages.length === 0) {
    if (!onAddPhotos) return <EmptyGalleryStatic label={t('detailView.gallery.empty')} />;
    return (
      <EmptyGalleryDropzone
        label={t('detailView.gallery.empty')}
        hint={uploading ? t('detailView.gallery.uploading') : t('detailView.gallery.addHint')}
        uploading={uploading}
        onFiles={onAddPhotos}
      />
    );
  }

  const thumbnails = allImages.slice(1, THUMBNAIL_COUNT + 1);
  const shown = Math.min(allImages.length, THUMBNAIL_COUNT + 1);
  const remaining = allImages.length - shown;

  return (
    <>
      <div className="flex min-w-0 flex-col gap-(--space-2)" data-testid="property-gallery">
        {/*
          16:10, and steppable in place.
          
          The cover used to be 3:2 and frozen on the first photo, so seeing
          the second one meant opening the viewer and closing it again. The
          arrows step here; tapping the photo still opens it full screen,
          now on the one you are looking at rather than always the first.
        */}
        <div className="relative w-full">
          <UnstyledButton
            type="button"
            onClick={() => setLightboxIndex(coverIndex)}
            aria-label={t('detailView.gallery.mainPhoto')}
            className="relative flex aspect-[16/10] w-full items-center justify-center overflow-hidden rounded-(--radius-lg) border border-solid border-[var(--border-subtle)] bg-[var(--surface-card)]"
          >
            <Image
              src={allImages[coverIndex]}
              alt={t('detailView.gallery.photoN', { index: coverIndex + 1 })}
              className="h-full w-full object-cover object-center"
            />
            <Text
              component="span"
              data-testid="gallery-photo-count"
              className="absolute left-(--space-3) top-(--space-3) inline-flex h-[22px] items-center rounded-(--radius-full) bg-[var(--surface-inverse)] px-(--space-2-5) text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-[var(--text-inverse)]"
            >
              {coverIndex + 1} / {allImages.length}
            </Text>
          </UnstyledButton>

          {allImages.length > 1 && (
            <>
              <CoverArrow
                side="left"
                label={t('detailView.gallery.previous')}
                disabled={coverIndex === 0}
                onClick={() => setCoverIndex((i) => Math.max(0, i - 1))}
              />
              <CoverArrow
                side="right"
                label={t('detailView.gallery.next')}
                disabled={coverIndex === allImages.length - 1}
                onClick={() => setCoverIndex((i) => Math.min(allImages.length - 1, i + 1))}
              />
            </>
          )}
        </div>

        {thumbnails.length > 0 && (
          <div
            data-testid="gallery-thumbnails"
            className="grid grid-cols-4 gap-(--space-2)"
          >
            {thumbnails.map((img, idx) => {
              const showMore = idx === thumbnails.length - 1 && remaining > 0;
              return (
                <UnstyledButton
                  key={img}
                  type="button"
                  onClick={() => setLightboxIndex(idx + 1)}
                  aria-label={
                    showMore
                      ? t('detailView.gallery.viewAllCount', { count: allImages.length })
                      : t('detailView.gallery.photoN', { index: idx + 2 })
                  }
                  data-testid={showMore ? 'gallery-more' : undefined}
                  className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-(--radius-md) border border-solid border-[var(--border-subtle)] bg-[var(--surface-card)]"
                >
                  <Image
                    src={img}
                    alt={t('detailView.gallery.photoN', { index: idx + 2 })}
                    className="h-full w-full object-cover object-center"
                  />
                  {showMore && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[var(--overlay-scrim)]">
                      <Text
                        component="span"
                        className="text-[length:var(--text-body)] [font-weight:var(--font-bold)] leading-none text-[var(--text-inverse)]"
                      >
                        +{remaining}
                      </Text>
                    </div>
                  )}
                </UnstyledButton>
              );
            })}
          </div>
        )}
      </div>

      {lightboxIndex !== null && (
        <GalleryLightbox
          images={allImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex(lightboxIndex - 1)}
          onNext={() => setLightboxIndex(lightboxIndex + 1)}
          onSelect={setLightboxIndex}
        />
      )}
    </>
  );
};

/**
 * One of the cover's two stepping controls.
 *
 * Absolutely positioned over the photo rather than beside it — there is no
 * width to spare at 400px — and translucent-dark so it reads on a photo of
 * any colour. Disabled at the ends instead of wrapping: wrapping from the
 * last photo to the first makes "am I at the end" unanswerable.
 */
function CoverArrow({ side, label, disabled, onClick }: {
  side: 'left' | 'right';
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      type="button"
      aria-label={label}
      data-testid={`gallery-cover-${side}`}
      disabled={disabled}
      onClick={onClick}
      className="absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full disabled:opacity-30"
      style={{
        [side]: 'var(--space-2)',
        background: 'color-mix(in srgb, var(--surface-inverse) 55%, transparent)',
        color: 'var(--text-inverse)',
      }}
    >
      {side === 'left'
        ? <ChevronLeftIcon width={20} height={20} />
        : <ChevronRightIcon width={20} height={20} />}
    </UnstyledButton>
  );
}
